import React from 'react';
import { RandomizeAction, RandomizedUrl } from '../../../../types/qr.types';
import { Input } from '../../../Input';

interface RandomizeActionEditorProps {
  action: RandomizeAction;
  onChange: (action: RandomizeAction) => void;
}

export function RandomizeActionEditor({ action, onChange }: RandomizeActionEditorProps) {
  const handleUrlChange = (index: number, field: keyof RandomizedUrl, value: string | number) => {
    const newUrls = [...action.urls];
    newUrls[index] = { ...newUrls[index], [field]: value };
    onChange({ ...action, urls: newUrls });
  };

  const addUrl = () => {
    onChange({
      ...action,
      urls: [...action.urls, { url: '', weight: 1 }],
    });
  };

  const removeUrl = (index: number) => {
    if (action.urls.length <= 2) return; // Keep minimum 2 URLs
    const newUrls = action.urls.filter((_, i) => i !== index);
    onChange({ ...action, urls: newUrls });
  };

  const totalWeight = action.urls.reduce((sum, u) => sum + (u.weight || 1), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[--clinqr-text-secondary]">A/B Test URLs</label>
        <button
          type="button"
          onClick={addUrl}
          className="text-sm text-[--clinqr-primary] hover:opacity-80 flex items-center gap-1 font-medium"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add URL
        </button>
      </div>

      <div className="space-y-3">
        {action.urls.map((urlEntry, index) => {
          const percentage = totalWeight > 0 ? Math.round((urlEntry.weight / totalWeight) * 100) : 0;
          return (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="example.com/variant-a"
                  value={urlEntry.url}
                  onChange={(e) => handleUrlChange(index, 'url', e.target.value)}
                  label={index === 0 ? 'URL' : ''}
                />
              </div>
              <div className="w-24">
                <label className={`block text-sm font-medium text-[--clinqr-text-secondary] mb-2 ${index !== 0 ? 'opacity-0' : ''}`}>
                  Weight
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={urlEntry.weight}
                  onChange={(e) => handleUrlChange(index, 'weight', parseInt(e.target.value) || 1)}
                  className="w-full h-10 px-3 rounded-lg border border-[--clinqr-border]/20 focus:outline-none focus:border-[--clinqr-primary] bg-white text-center text-[--clinqr-text-primary]"
                />
              </div>
              <div className="w-16 text-center">
                <label className={`block text-sm font-medium text-[--clinqr-text-secondary] mb-2 ${index !== 0 ? 'opacity-0' : ''}`}>
                  %
                </label>
                <div className="h-10 flex items-center justify-center text-sm font-medium text-[--clinqr-text-secondary]">
                  {percentage}%
                </div>
              </div>
              <div className="flex items-end">
                <div className={`${index !== 0 ? '' : 'mt-8'}`}>
                  <button
                    type="button"
                    onClick={() => removeUrl(index)}
                    disabled={action.urls.length <= 2}
                    className={`p-2 rounded-lg transition-colors ${
                      action.urls.length <= 2
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-[--clinqr-bg-subtle] border border-[--clinqr-border]/10 rounded-lg p-3">
        <p className="text-sm text-[--clinqr-text-secondary]">
          <strong className="text-[--clinqr-text-primary]">How it works:</strong> When this rule matches, users are randomly redirected to one of the URLs above.
          Higher weights mean higher probability. Equal weights distribute traffic evenly.
        </p>
      </div>
    </div>
  );
}
